import { useState } from 'react';
import { cn } from '@simplywallst/ui-core/next';
import { bisector } from 'd3-array';
import { localPoint } from '@visx/event';
import { getChartCssVar } from '../hooks/useNarrativeChartStyles';
import { TooltipsInteractive, TooltipsStatic } from './Tooltips';
import type { ScaleTime } from 'd3-scale';
import type { getLastData, getSelectedData } from '../utils';

export type HoverParam = {
  time: number;
  price: number | null;
  fairValues: (number | null)[];
};

export interface ChartOverlayProps {
  height: number;
  isMarkerPinned: boolean;
  keys: number[];
  showPriceBlips: boolean;
  lastData: ReturnType<typeof getLastData>;
  selectedData: ReturnType<typeof getSelectedData>;
  narrativeLabels: string[];
  xScale: ScaleTime<number, number>;
  intrinsicDiscount: React.ReactNode;
  onClick: () => void;
  onSelect: (index: number) => void;
}

const bisectDate = bisector((d) => d).left;

// Simple mobile detection based on touch support
const isTouch = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const getCoordinatesAtPoint = (
  e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>
) => {
  const point = localPoint(e);

  let x = 0;
  let y = 0;

  if (point !== null && point.x >= 0) {
    x = point.x;
    y = point.y;
  }

  return { x, y };
};

export const ChartOverlay = ({
  height,
  isMarkerPinned,
  keys,
  lastData,
  selectedData,
  narrativeLabels,
  xScale,
  showPriceBlips,
  intrinsicDiscount,
  onClick,
  onSelect,
}: ChartOverlayProps) => {
  const [index, setIndex] = useState<number>(Infinity);
  const [mouseY, setMouseY] = useState<number>(0);

  const setCurrentPoint = (
    e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>
  ) => {
    const { x, y } = getCoordinatesAtPoint(e);

    setMouseY(y);

    const nextIndex = bisectDate(keys, xScale.invert(x));
    const isSelectable = nextIndex !== index && nextIndex <= keys.length - 1;

    if (isSelectable) {
      setIndex(nextIndex);
      onSelect(nextIndex);
    }
  };

  const showInteractiveDisplay =
    index !== Infinity && index >= 0 && index < keys.length;

  return (
    <>
      <TooltipsStatic
        lastData={lastData}
        height={height}
        isMarkerPinned={isMarkerPinned}
        narrativeLabels={narrativeLabels}
      />
      <TooltipsInteractive
        isMarkerPinned={isMarkerPinned}
        selectedData={selectedData}
        height={height}
        intrinsicDiscount={intrinsicDiscount}
        mouseY={mouseY}
      />
      <svg height={height} className="block w-full touch-pan-y">
        {/* Standby display */}
        <g
          transform={`translate(${lastData.lastX} 0)`}
          className={cn(
            'pointer-events-none transition-opacity duration-150 ease-out group-hover/chart:opacity-0',
            isMarkerPinned && 'opacity-0'
          )}
        >
          {/* Vertical line */}
          <line
            y2={height}
            strokeWidth="1.5"
            strokeDasharray="3, 4"
            stroke={getChartCssVar('POINT_STROKE')}
          />
          {/* Fair value blip */}
          {lastData.fairValueYs[selectedData.fairValueIndex] && (
            <circle
              transform={`translate(0 ${
                lastData.fairValueYs[selectedData.fairValueIndex]
              })`}
              r="4"
              fill={getChartCssVar('VALUATION_COLOR')}
            />
          )}
          {/* Price blip */}
          <circle
            r="4"
            transform={`translate(0 ${lastData.priceY})`}
            fill={getChartCssVar('PRICE_COLOR')}
          />
        </g>

        {/* Interactive display */}
        {showInteractiveDisplay && (
          <g
            transform={`translate(${selectedData.x} 0)`}
            className={cn(
              'pointer-events-none opacity-0 transition-opacity duration-150 ease-out group-hover/chart:opacity-100',
              isMarkerPinned && 'opacity-100'
            )}
          >
            {/* Vertical line at current selection point */}
            <line
              y2={height}
              strokeWidth="1.5"
              strokeDasharray={isMarkerPinned ? undefined : '3, 4'}
              stroke={getChartCssVar('POINT_STROKE')}
            />
            {/* Price current at selection point */}
            <circle
              transform={`translate(0 ${selectedData.getPriceY(keys[index])})`}
              r="5"
              fill={getChartCssVar('PRICE_COLOR')}
              className="brightness-125"
            />
            {/* Fair value at current selection point */}
            {selectedData.fairValues[selectedData.fairValueIndex] && (
              <circle
                transform={`translate(0 ${selectedData.getFairValueYs[
                  selectedData.fairValueIndex
                ](keys[index])})`}
                r="5"
                fill={getChartCssVar('VALUATION_COLOR')}
                className="brightness-125"
              />
            )}
          </g>
        )}
        {/* Small blips on price line at each date point, shown on hover */}
        {showPriceBlips && (
          <g
            className={cn(
              'pointer-events-none opacity-0 transition-opacity duration-150 ease-out group-hover/chart:opacity-100'
            )}
          >
            {keys.map((key, index) => (
              <circle
                key={`price-blip-${key}-${index}`}
                transform={`translate(${xScale(
                  key || 0
                )} ${selectedData.getPriceY(key)})`}
                r="3"
                fill={getChartCssVar('PRICE_COLOR')}
              />
            ))}
          </g>
        )}
        {/* Interaction area */}
        <rect
          width="100%"
          height="100%"
          fill="transparent"
          onMouseMove={(e) => !isMarkerPinned && setCurrentPoint(e)}
          onMouseLeave={() => !isMarkerPinned && setIndex(Infinity)}
          onTouchStart={setCurrentPoint}
          onTouchMove={setCurrentPoint}
          onClick={(e) => {
            setCurrentPoint(e);
            // Avoid triggering pin events on mobile (desktop feature only)
            if (!isTouch()) onClick?.();
          }}
          className={cn(
            'touch-none', // Avoid passive listener
            isMarkerPinned ? 'cursor-pointer' : 'cursor-crosshair'
          )}
        />
      </svg>
    </>
  );
};
